import axios from 'axios';
import Jsona from 'jsona';
import WorkspaceUtils from '../utils/workspace-utils';
import TubeError from '@/lib/tube-error';

class PublicClient {
  private dataFormatter: Jsona;
  private _workspaceName!: string;

  constructor() {
    this.dataFormatter = new Jsona();
  }

  setWorkspaceName(name) {
    this._workspaceName = name;
  }

  get workspaceName() {
    return (
      this._workspaceName ||
      WorkspaceUtils.parseWorkspaceName(window.location.hostname)
    );
  }

  getShareableLinkInfo(shareableLinkId: string) {
    return this._get(`/shareable_links/${shareableLinkId}`, {});
  }

  getShareablePdfUrl(videoId: string) {
    return this._get(`/shareable_links/${videoId}/pdf`, {}, true, true);
  }

  getVideoInfo(shareableLinkId: string, videoId: string, isMobile: boolean) {
    return this._get(
      `/shareable_links/${shareableLinkId}/videos/${videoId}?is_mobile=${isMobile}`,
      {}
    );
  }

  getThumbnailUrl(shareableLinkId: string, videoId: string) {
    return this._get(
      `/shareable_links/${shareableLinkId}/videos/${videoId}/thumbnail_url`,
      {},
      true,
      true,
      true
    );
  }

  trackActivity(
    shareableLinkId: string,
    videoId: string,
    stepId: string,
    fingerprint: string,
    sessionId: string
  ) {
    return this._post(
      `/analytics/${videoId}/`,
      {
        Authorization: `Bearer ${shareableLinkId}`
      },
      {
        step_id: stepId,
        fingerprint: fingerprint,
        session_id: sessionId
      },
      true,
      true
    );
  }

  trackEngagement(
    shareableLinkId: string,
    videoId: string,
    fingerprint: string,
    sessionId: string,
    duration: number,
    dimension1: string,
    currentTime: number
  ) {
    return this._post(
      `/analytics/${videoId}/engagements`,
      {
        Authorization: `Bearer ${shareableLinkId}`
      },
      {
        duration: duration,
        fingerprint: fingerprint,
        session_id: sessionId,
        medium: window.top == window ? 'link' : 'embed',
        dimension_1: dimension1,
        current_time: currentTime
      },
      true,
      true
    );
  }

  fetchVideoStats(videoId, fingerprint, shareId) {
    return this._get(
      `/shareable_links/${shareId}/feedback?video_id=${videoId}&fingerprint=${fingerprint}`
    );
  }

  updateFeedback(videoId, fingerprint, satisfaction, shareId) {
    return this._post(
      `/shareable_links/${shareId}/feedback`,
      {},
      {
        fingerprint,
        satisfaction,
        video_id: videoId
      }
    );
  }

  private _get(
    path: string,
    headers = {},
    tenancy = true,
    skipConversion = false,
    auth = true
  ) {
    const url = WorkspaceUtils.workspaceApiUrl(this.workspaceName);
    return this.handleError(
      axios({
        method: 'GET',
        url: url + path,
        // timeout: 6000,
        headers: Object.assign(
          {
            'Content-Type': 'application/json'
          },
          headers
        )
      }).then(({ data }) => {
        return skipConversion
          ? data
          : (this.dataFormatter.deserialize(data) as any);
      })
    );
  }

  private _post(
    path: string,
    headers = {},
    body = {},
    tenancy = true,
    skipConversion = false,
    auth = true
  ) {
    const url = WorkspaceUtils.workspaceApiUrl(this.workspaceName);
    return this.handleError(
      axios({
        method: 'POST',
        url: url + path,
        // timeout: 6000,
        headers: Object.assign(
          {
            'Content-Type': 'application/json'
          },
          headers
        ),
        data: body
      }).then(({ data }) => {
        return skipConversion
          ? data
          : (this.dataFormatter.deserialize(data) as any);
      })
    );
  }

  private handleError(promise) {
    return promise.catch(error => {
      if (!error.response) {
        throw error;
      }
      throw new TubeError(error.response.data, error.response.status);
    });
  }
}

export default new PublicClient();
